import { Disclosure, Transition } from '@headlessui/react';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { MouseEvent, ReactNode, useCallback, useContext, useMemo, useRef, useState } from 'react';
import React from 'react';
import { Link } from 'react-router-dom';
import { useCurrentUserQuery } from '@/modules/auth/queries/user.tsx';
import Badge from '@/modules/common/components/Badge.tsx';
import { CreatedByAiLabel } from '@/modules/common/components/CreatedByAiLabel.tsx';
import OverdueTaskBadge from '@/modules/common/components/OverdueTaskBadge.tsx';
import TaskDates from '@/modules/common/components/TaskDates.tsx';
import { checkIfTaskAssignedToUser } from '@/modules/common/helpers/checkIfTaskAssignedToUser.ts';
import { checkIfTaskOverdueSoon } from '@/modules/common/helpers/checkIfTaskOverdueSoon.ts';
import { commonPaths } from '@/modules/common/routes.tsx';
import ArchivedItemAction from '@/modules/admin-dashboard/components/ArchivedItemAction.tsx';
import UsersAvatarsSet from '@/modules/admin-dashboard/components/UsersAvatarsSet.tsx';
import TaskItemDetailedInfo from '@/modules/admin-dashboard/components/items/TaskItemDetailedInfo.tsx';
import TaskItemResultInfo from '@/modules/admin-dashboard/components/items/TaskItemResultInfo.tsx';
import RemindAssigneeModal from '@/modules/admin-dashboard/components/modals/RemindAssigneeModal.tsx';
import { priorityColors } from '@/modules/admin-dashboard/components/modals/TaskModal.tsx';
import { UpdateTaskModal } from '@/modules/admin-dashboard/components/modals/UpdateTaskModal.tsx';
import {
  useCreateTaskMutation,
  useDeleteTaskMutation,
  useRestoreTaskMutation,
} from '@/modules/admin-dashboard/queries/tasks.ts';
import { ConfirmModal, DropdownMenu, DropdownMenuItem, ModalProviderContext, TooltipNew } from 'ui';
import { useHiddenBadges } from 'ui/hooks';
import { ChevronDownIcon, DotsHorizontalIcon } from 'ui/icons';
import {
  TaskListWithRelationsResponse,
  TaskListWithRelationsSummaryResponse,
  TaskResult,
} from 'compliance-api';

interface TaskItemProps {
  archiveEnabled?: boolean;
  isUpcoming?: boolean;
  task: TaskListWithRelationsResponse | TaskListWithRelationsSummaryResponse;
  clientId?: number;
}

const TaskItem = ({ task, archiveEnabled, isUpcoming, clientId }: TaskItemProps) => {
  const badgesRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const { openModal } = useContext(ModalProviderContext);

  const {
    task_results,
    users,
    registers,
    start_date,
    name,
    priority,
    current_start_date,
    overdue_date,
    deleted_at,
    created_with_ai,
  } = task;

  const { data: currentUser } = useCurrentUserQuery();
  const { mutateAsync: createTask } = useCreateTaskMutation();
  const { mutateAsync: deleteTask } = useDeleteTaskMutation();

  const isMyTask = checkIfTaskAssignedToUser(currentUser, users || []);
  const isTaskOverdueSoon = checkIfTaskOverdueSoon(task);
  const overdueResultsCount = useMemo(
    () => (task_results || []).filter(({ result }) => result === TaskResult.Due).length,
    [task_results],
  );

  const badges = useMemo(() => {
    const res = [];

    if (overdueResultsCount) {
      res.push(<OverdueTaskBadge count={overdueResultsCount} />);
    }
    if (isTaskOverdueSoon) {
      res.push(
        <Badge type="warning" className="block">
          Overdue soon
        </Badge>,
      );
    }
    if (isMyTask) {
      res.push(
        <Badge type="info" className="!text-[#0369A1]">
          My task
        </Badge>,
      );
    }

    const registerBadges = (registers || []).map(register => (
      <Badge
        type="neutral"
        key={register.id}
        className="text-[#6B6B6B] max-w-[90px] !block truncate leading-5"
      >
        {register.name}
      </Badge>
    ));

    const allBadges = [...res, ...registerBadges];

    if (created_with_ai) {
      allBadges.push(<CreatedByAiLabel created_with_ai={created_with_ai} />);
    }

    return allBadges;
  }, [isMyTask, isTaskOverdueSoon, overdueResultsCount, registers, created_with_ai]);

  const hiddenBadgesCount = useHiddenBadges(badges.length, badgesRef);

  const isPossibleToComplete = dayjs().isAfter(start_date);

  const handleDuplicateTask = useCallback(async () => {
    const additionalContent = ' (Duplicated)';
    const resolveName = name.substring(0, 50 - (additionalContent.length + 1));

    await createTask({ ...task, name: `${resolveName} ${additionalContent}` });
  }, [createTask, name, task]);

  const options = useMemo(() => {
    const res: DropdownMenuItem<ReactNode>[] = [
      {
        label: 'Edit',
        onClick: () => openModal(UpdateTaskModal, { id: task?.id, clientId }),
      },
      {
        label: 'Duplicate',
        onClick: () =>
          openModal(ConfirmModal, {
            onConfirm: handleDuplicateTask,
            title: (
              <p>
                Are you sure you want to duplicate <span className="text-blue-700">{name}</span> ?
              </p>
            ),
          }),
      },
      {
        label: 'Archive',
        onClick: () =>
          openModal(ConfirmModal, {
            onConfirm: async () => {
              deleteTask(task?.id);
            },
            title: (
              <p>
                Are you sure you want to archive <span className="text-blue-700">{name}</span>?
              </p>
            ),
          }),
      },
    ];

    if (users?.length) {
      res.push({
        label: (
          <TooltipNew
            className={classNames({ 'text-gray-500': !isPossibleToComplete })}
            text="This task cannot be completed yet."
            disabled={isPossibleToComplete}
          >
            Remind assignee
          </TooltipNew>
        ),
        disabled: !isPossibleToComplete,
        onClick: () => {
          isPossibleToComplete ? openModal(RemindAssigneeModal, { task }) : undefined;
        },
      });
    }

    return res;
  }, [
    clientId,
    deleteTask,
    handleDuplicateTask,
    isPossibleToComplete,
    name,
    openModal,
    task,
    users?.length,
  ]);

  const handleOpenUpdateTaskModal = useCallback(
    (e: MouseEvent<HTMLSpanElement>) => {
      e.stopPropagation();
      openModal(UpdateTaskModal, { defaultDisabled: true, id: task?.id, clientId });
    },
    [clientId, openModal, task?.id],
  );

  const handleToggleOpen = useCallback(() => {
    setIsOpen(open => !open);
  }, []);
  const taskResultsItems = useMemo(() => {
    return (users || [])
      .map(user => {
        const userTaskResults = (task_results || []).filter(
          ({ completed_by_user_id }) => user.id === completed_by_user_id,
        );

        return userTaskResults.length
          ? userTaskResults.map(result => ({ result, user }))
          : [{ user, result: null }];
      })
      .flat();
  }, [users, task_results]);

  const additionalInfo = useMemo(() => {
    if (archiveEnabled && task?.task_results?.length) {
      return (
        <div className="border-t max-h-[400px] overflow-y-auto">
          {taskResultsItems
            .filter(({ result }) => result && result.deleted_at)
            .map(({ result, user }) => (
              <div
                key={`${task.id}_${result?.id}`}
                className={classNames('px-4 [&:last-child]:rounded-b-xl', {
                  'bg-gray-100': result?.deleted_at,
                })}
              >
                <TaskItemResultInfo user={user} task={task} result={result} />
              </div>
            ))}
        </div>
      );
    } else {
      return <TaskItemDetailedInfo task={task} />;
    }
  }, [archiveEnabled, task, taskResultsItems]);
  return (
    <div
      className={classNames(
        'sm:border sm:rounded-xl border-gray-200 [&:not(:last-child)]:border-b',
        { 'bg-gray-100': deleted_at },
      )}
    >
      <div
        className={classNames(
          'pb-2 pl-2 sm:pb-4 sm:px-6 sm:pt-4 relative w-full grid grid-cols-[auto_1fr_auto_auto_auto] items-center gap-4  overflow-hidden cursor-pointer',
        )}
        onClick={deleted_at ? undefined : handleToggleOpen}
      >
        <div
          className={classNames(
            'absolute left-0 top-0 w-[11px] h-full sm:rounded-tl-xl',
            { 'sm:rounded-bl-xl': !isOpen },
            priorityColors[priority],
          )}
          style={{ clipPath: 'inset(0px 6px 0px 0px)' }}
        />
        <img
          src={ChevronDownIcon}
          alt="open"
          width={24}
          className={classNames('select-none hover:opacity-80 min-w-6', {
            '-rotate-90': !isOpen,
            'opacity-0 hover:!opacity-0': deleted_at,
          })}
        />
        <div className="space-y-1.5 overflow-hidden">
          {isUpcoming && <span className="text-sm h-fit">{name}</span>}
          {!isMyTask && !isUpcoming && (
            <span
              className="text-sm hover:underline cursor-pointer h-fit"
              onClick={handleOpenUpdateTaskModal}
            >
              {name}
            </span>
          )}
          {isMyTask && !isUpcoming && (
            <Link
              className="text-sm hover:underline h-fit"
              to={{ pathname: commonPaths.dynamic.task(task.id), search: location.search }}
            >
              {name}
            </Link>
          )}
          <div className="flex gap-1.5 text-nowrap" ref={badgesRef}>
            {badges.map((badge, i, { length }) => (
              <React.Fragment key={i}>
                {length - i <= hiddenBadgesCount ? null : badge}
              </React.Fragment>
            ))}
            {!!hiddenBadgesCount && <Badge type="neutral">+ {hiddenBadgesCount}</Badge>}
          </div>
        </div>
        <TaskDates current_start_date={current_start_date} overdue_date={overdue_date} />
        {!deleted_at && users && (
          <div className="w-[100px] hidden lg:block">
            <UsersAvatarsSet users={users} className="[&>div:first-child]:!justify-start" />
          </div>
        )}
        {deleted_at ? (
          <ArchivedItemAction id={task.id} mutation={useRestoreTaskMutation} />
        ) : (
          <DropdownMenu
            autoPlace
            items={options}
            button={
              <button className="flex items-center justify-center">
                <img src={DotsHorizontalIcon} alt="Loading..." />
              </button>
            }
            className="w-[180px] divide-white"
          />
        )}
      </div>
      <Disclosure>
        <Transition
          show={isOpen}
          enter="transition duration-100 ease-out"
          enterFrom="transform scale-95 opacity-0"
          enterTo="transform scale-100 opacity-100"
          leave="transition duration-75 ease-out"
          leaveFrom="transform scale-100 opacity-100"
          leaveTo="transform scale-95 opacity-0"
        >
          <Disclosure.Panel static>{additionalInfo}</Disclosure.Panel>
        </Transition>
      </Disclosure>
    </div>
  );
};
export default TaskItem;
